import React from 'react';
import { styled } from '@compiled/react';
import { graphql, useFragment } from 'react-relay';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button';
import EmptyState from '@atlaskit/empty-state';
import { token } from '@atlaskit/tokens';
import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import type { emptyView_softwareReleasesVersionDetailIssueList$key } from '@atlassian/jira-relay/src/__generated__/emptyView_softwareReleasesVersionDetailIssueList.graphql';
import { AddIssuesToVersion } from '@atlassian/jira-software-releases-add-issues-to-version/src/ui/async.tsx';
import { useFilterSortingQueryParams } from '@atlassian/jira-software-releases-version-detail-issue-list-query-param-controller/src/controllers/filter-sorting-query-param/index.tsx';
import noIssuesInVersionImageUrl from './assets/noIssuesInVersion.svg';
import noSearchResultsImageUrl from './assets/noSearchResults.svg';
import messages from './messages.tsx';

type Props = {
	jiraVersion: emptyView_softwareReleasesVersionDetailIssueList$key;
	isAnyFilterApplied: boolean;
	textFilter: string | null;
};

export const EmptyView = ({ jiraVersion, isAnyFilterApplied, textFilter }: Props) => {
	const { clearAll } = useFilterSortingQueryParams();

	const { formatMessage } = useIntl();

	const version = useFragment(
		graphql`
			fragment emptyView_softwareReleasesVersionDetailIssueList on JiraVersion {
				canAddAndRemoveIssues @required(action: LOG)
				...ui_softwareReleasesAddIssuesToVersion_AddIssuesToVersion
			}
		`,
		jiraVersion,
	);

	return (
		<Container data-testid="software-releases-version-detail-issue-list.ui.empty-view">
			{isAnyFilterApplied ? (
				<EmptyState
					headingLevel={2}
					header={
						textFilter !== null
							? formatMessage(messages.noIssuesMatchTextFilterHeader, { textFilter })
							: formatMessage(messages.noIssuesMatchFilterHeader)
					}
					description={
						textFilter !== null
							? formatMessage(messages.noIssuesMatchTextFilterDescription)
							: formatMessage(messages.noIssuesMatchFilterDescription)
					}
					imageUrl={noSearchResultsImageUrl}
					imageWidth={gridSize * 8}
					imageHeight={gridSize * 11.5}
					primaryAction={
						<Button
							appearance="primary"
							onClick={(_, analyticsEvent: UIAnalyticsEvent) => {
								clearAll();
								fireUIAnalytics(analyticsEvent, 'clearFilter');
							}}
						>
							{formatMessage(messages.clearFilters)}
						</Button>
					}
				/>
			) : (
				<EmptyState
					headingLevel={2}
					header={formatMessage(messages.noIssuesInVersionHeader)}
					description={formatMessage(messages.noIssuesInVersionDescription)}
					imageUrl={noIssuesInVersionImageUrl}
					imageWidth={gridSize * 16}
					imageHeight={gridSize * 13}
					primaryAction={
						version?.canAddAndRemoveIssues === true ? (
							<AddIssuesToVersion appearance="primary" jiraVersion={version} />
						) : undefined
					}
				/>
			)}
		</Container>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled.div({
	paddingTop: token('space.250'),
});
